import { z } from 'zod'
import Service, { ClientException } from './base'

const VALIDATE_BOOKING_PATH = '/api/v1/validate-booking'
const EMAIL_INVALID = 'invalid'
const UNPROCESSABLE_ENTITY = 422

const validationErrorsSchema = z.object({
  errors: z.object({
    email: z.string().optional()
  }).optional()
})

/** `unknown` means the service could not give a verdict (network error, 5xx, unexpected body). */
export type EmailDeliverability = 'deliverable' | 'undeliverable' | 'unknown'

export default class ValidationService extends Service {
  /**
   * Asks the backend whether the mailbox behind `emailAddress` is likely deliverable.
   * Only an explicit `422 { errors: { email: 'invalid' } }` yields `undeliverable`.
   */
  async validateEmailAddress (emailAddress: string): Promise<EmailDeliverability> {
    try {
      await this.post(VALIDATE_BOOKING_PATH, { main: { emailAddress } })
      return 'deliverable'
    } catch (e) {
      if (!(e instanceof ClientException) || e.response?.status !== UNPROCESSABLE_ENTITY) {
        return 'unknown'
      }
      // Any other 422 shape (e.g. request schema rejection) is not a verdict about the mailbox
      const parsed = validationErrorsSchema.safeParse(await e.jsonPromise?.catch(() => undefined))
      return parsed.success && parsed.data.errors?.email === EMAIL_INVALID ? 'undeliverable' : 'unknown'
    }
  }
}
